import { useId, useMemo } from 'react';
import ActionButton from '@/components/Button/ActionButton';
import IconButton from '@/components/Button/IconButton';
import { AddressReadOnly, type AddressData } from '@/components/Form/AddressForm/-components/AddressReadOnly';
import { AddressForm } from '@/components/Form/AddressForm/AddressForm';
import { AddressFormContext } from '@/components/Form/AddressForm/AddressFormContext';
import Icon from '@/components/Icon/Icon';
import Input from '@/components/Input/Input';
import { InputPhoneNumber } from '@/components/Input/Phone/InputPhoneNumber';
import type { PhoneValue } from '@/components/Input/Phone/state.machine';
import { HStack, VStack } from '@/components/Layout';
import { Text } from '@/components/Typography/Text';
import { isSupportedCountry } from '@/constants/country';
import type { EmergencyContactListItem } from '@/features/section-attribute/types';
import { useCommonTranslation } from '@/hooks/useCommonTranslation';
import { useFormattedPhoneNumber } from '@/hooks/useFormattedPhoneNumber';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { CFormItemBase, type CFormItemProps, buildCFormItem } from '../CFormItem';
const JAPAN_DIAL_CODE = '81';
type EmergencyContactPhone = EmergencyContactListItem['phones'][number];
type Props = CFormItemProps & {
  value?: EmergencyContactListItem[];
  onChange: (value: EmergencyContactListItem[]) => void;
};
function toPhoneValue(ec: EmergencyContactPhone): PhoneValue {
  return {
    dialCode: ec.dialCode ?? JAPAN_DIAL_CODE,
    phoneNumber: ec.phoneNumber ?? ''
  };
}
function hasAddress(contact: EmergencyContactListItem): boolean {
  return !!(contact.countryCode || contact.postalCode || contact.stateProvinceCode || contact.city || contact.streetAddress || contact.addressLine2);
}
function ContactAddressReadOnly({
  contact
}: {
  contact: EmergencyContactListItem;
}) {
  const tc = useCommonTranslation();
  const supportedCountry = contact.countryCode && isSupportedCountry(contact.countryCode) ? contact.countryCode : undefined;
  const addressData: AddressData = useMemo(() => {
    const countryName = supportedCountry ? tc(`country.${supportedCountry}`) : contact.countryCode ?? undefined;
    return {
      countryCode: contact.countryCode ?? undefined,
      countryName,
      postalCode: contact.postalCode ?? undefined,
      stateProvinceLabel: contact.stateProvinceName ?? undefined,
      city: contact.city ?? undefined,
      streetAddress: contact.streetAddress ?? undefined,
      addressLine2: contact.addressLine2 ?? undefined
    };
  }, [supportedCountry, contact.countryCode, contact.postalCode, contact.stateProvinceName, contact.city, contact.streetAddress, contact.addressLine2, tc]);
  return <AddressReadOnly data={addressData} />;
}
function ContactAddressEdit({
  contact,
  onUpdate
}: {
  contact: EmergencyContactListItem;
  onUpdate: (patch: Partial<EmergencyContactListItem>) => void;
}) {
  const isRequired = hasAddress(contact);
  return <AddressFormContext.Provider value={{
    values: {
      countryCode: contact.countryCode && isSupportedCountry(contact.countryCode) ? contact.countryCode : undefined,
      postalCode: contact.postalCode ?? undefined,
      stateProvinceCode: contact.stateProvinceCode ?? undefined,
      city: contact.city ?? undefined,
      streetAddress: contact.streetAddress ?? undefined,
      addressLine2: contact.addressLine2 ?? undefined
    },
    errors: {},
    touched: {
      countryCode: true,
      postalCode: true,
      stateProvinceCode: true,
      city: true,
      streetAddress: true,
      addressLine2: true
    },
    onChange: changes => {
      onUpdate(changes);
    },
    onBlur: () => {},
    validate: () => true
  }}>
      <AddressForm isCountryRequired={isRequired} isPostalCodeRequired={isRequired} isStateProvinceRequired={isRequired} isCityRequired={isRequired} isStreetAddressRequired={isRequired} />
    </AddressFormContext.Provider>;
}
function FormattedContactPhone({
  phone
}: {
  phone: EmergencyContactPhone;
}) {
  const pv = toPhoneValue(phone);
  const {
    formattedFull
  } = useFormattedPhoneNumber(pv);
  return <HStack className="gap-2 items-center">
      <Icon iconName="Phone" className="h-4 w-4 text-primary-fill" />
      <Text variant="p">{formattedFull}</Text>
    </HStack>;
}
function createEmptyContact(): EmergencyContactListItem {
  return {
    lastName: '',
    firstName: '',
    relationship: '',
    phones: [{
      phoneType: null,
      dialCode: JAPAN_DIAL_CODE,
      phoneNumber: ''
    }],
    emails: [{
      email: ''
    }],
    countryCode: null,
    postalCode: null,
    stateProvinceCode: null,
    stateProvinceName: null,
    city: null,
    streetAddress: null,
    addressLine2: null
  };
}
export const CFormEmergencyContactListItem = buildCFormItem((props: Props) => {
  const tc = useCommonTranslation();
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.person.profile.itemCard'
  });
  const idPrefix = useId();
  const values = props.value ?? [];
  const updateContact = (index: number, patch: Partial<EmergencyContactListItem>) => {
    const next = [...values];
    next[index] = {
      ...next[index],
      ...patch
    };
    props.onChange(next);
  };
  const handleAdd = () => {
    props.onChange([...values, createEmptyContact()]);
  };
  const handleRemove = (index: number) => {
    props.onChange(values.filter((_, i) => i !== index));
  };
  const handlePhoneChange = (contactIdx: number, phoneIdx: number, pv: PhoneValue) => {
    const contact = values[contactIdx];
    const newPhones = [...contact.phones];
    newPhones[phoneIdx] = {
      ...newPhones[phoneIdx],
      dialCode: pv.dialCode,
      phoneNumber: pv.phoneNumber
    };
    updateContact(contactIdx, {
      phones: newPhones
    });
  };
  const handleAddPhone = (contactIdx: number) => {
    const contact = values[contactIdx];
    updateContact(contactIdx, {
      phones: [...contact.phones, {
        phoneType: null,
        dialCode: JAPAN_DIAL_CODE,
        phoneNumber: ''
      }]
    });
  };
  const handleRemovePhone = (contactIdx: number, phoneIdx: number) => {
    const contact = values[contactIdx];
    updateContact(contactIdx, {
      phones: contact.phones.filter((_, i) => i !== phoneIdx)
    });
  };
  const handleEmailChange = (contactIdx: number, emailIdx: number, email: string) => {
    const contact = values[contactIdx];
    const newEmails = [...contact.emails];
    newEmails[emailIdx] = {
      email
    };
    updateContact(contactIdx, {
      emails: newEmails
    });
  };
  const handleAddEmail = (contactIdx: number) => {
    const contact = values[contactIdx];
    updateContact(contactIdx, {
      emails: [...contact.emails, {
        email: ''
      }]
    });
  };
  const handleRemoveEmail = (contactIdx: number, emailIdx: number) => {
    const contact = values[contactIdx];
    updateContact(contactIdx, {
      emails: contact.emails.filter((_, i) => i !== emailIdx)
    });
  };
  const buildName = (contact: EmergencyContactListItem) => {
    return [contact.lastName, contact.firstName].filter(Boolean).join(' ');
  };
  const renderReadMode = () => {
    if (values.length === 0) {
      return <Text variant="pSmall" className="text-neutral-muted-on-fill">
          -
        </Text>;
    }
    return <VStack className="gap-4 w-full">
        {values.map((contact, index) => {
        const name = buildName(contact);
        const validPhones = contact.phones.filter(p => p.phoneNumber?.trim());
        const validEmails = contact.emails.filter(e => e.email?.trim());
        return <VStack key={`${idPrefix}-read-${index}`} className="gap-2 w-full">
              <VStack className="gap-1">
                {name && <Text variant="labelLarge">{name}</Text>}
                {contact.relationship && <HStack className="gap-1 items-center">
                    <Text variant="labelSmall" className="text-neutral-muted-on-fill">
                      {t('relationship')}:
                    </Text>
                    <Text variant="p">{contact.relationship}</Text>
                  </HStack>}
              </VStack>
              <HStack className="gap-6 items-start">
                <VStack className="flex-1 gap-1">
                  {validPhones.map((phone, pi) => <FormattedContactPhone key={`${idPrefix}-phone-${index}-${pi}`} phone={phone} />)}
                  {validEmails.map((em, ei) => <HStack key={`${idPrefix}-email-${index}-${ei}`} className="gap-2 items-center">
                      <Icon iconName="Mail" className="h-4 w-4 text-primary-fill" />
                      <Text variant="p">{em.email}</Text>
                    </HStack>)}
                </VStack>
                {hasAddress(contact) && <HStack className="flex-1 gap-2 items-start">
                    <Icon iconName="MapPin" className="h-4 w-4 shrink-0 text-primary-fill mt-0.5" />
                    <ContactAddressReadOnly contact={contact} />
                  </HStack>}
              </HStack>
            </VStack>;
      })}
      </VStack>;
  };
  const renderWriteMode = () => {
    return <VStack className="gap-4 w-full">
        {values.map((contact, cIdx) => <VStack key={`${idPrefix}-write-${cIdx}`} className="gap-3 border-l-2 border-neutral-muted-border pl-4 py-2 w-full">
            <HStack className="justify-between items-start">
              <Text variant="label" className="text-neutral-muted-on-fill">
                {t('emergencyContact')} {cIdx + 1}
              </Text>
              {!props.readonly && <ActionButton icon="Trash2Icon" variant="ghost" iconColor="muted" onClick={() => handleRemove(cIdx)} aria-label={tc('action.delete')} />}
            </HStack>

            <HStack className="gap-6 items-start w-full">
              <VStack className="flex-1 gap-3">
                <HStack className="gap-2">
                  <VStack className="flex-1 gap-1">
                    <Text variant="labelSmall" className="text-neutral-muted-on-fill">
                      {t('familyName')}
                    </Text>
                    <Input width="100%" value={contact.lastName ?? ''} onChange={e => updateContact(cIdx, {
                  lastName: e.target.value
                })} disabled={props.readonly} />
                  </VStack>
                  <VStack className="flex-1 gap-1">
                    <Text variant="labelSmall" className="text-neutral-muted-on-fill">
                      {t('givenName')}
                    </Text>
                    <Input width="100%" value={contact.firstName ?? ''} onChange={e => updateContact(cIdx, {
                  firstName: e.target.value
                })} disabled={props.readonly} />
                  </VStack>
                </HStack>

                <VStack className="gap-1">
                  <Text variant="labelSmall" className="text-neutral-muted-on-fill">
                    {t('relationship')}
                  </Text>
                  <Input width="100%" value={contact.relationship ?? ''} onChange={e => updateContact(cIdx, {
                relationship: e.target.value
              })} disabled={props.readonly} />
                </VStack>

                <VStack className="gap-2">
                  <Text variant="labelSmall" className="text-neutral-muted-on-fill">
                    {t('phone')}
                  </Text>
                  {contact.phones.map((phone, pIdx) => <HStack key={`${idPrefix}-wphone-${cIdx}-${pIdx}`} className="gap-2 items-center">
                      <InputPhoneNumber value={toPhoneValue(phone)} onChange={pv => handlePhoneChange(cIdx, pIdx, pv)} />
                      {!props.readonly && contact.phones.length > 1 && <IconButton icon="Trash2Icon" variant="ghost" size="Small" className="shrink-0" onClick={() => handleRemovePhone(cIdx, pIdx)} aria-label={tc('action.delete')} />}
                    </HStack>)}
                  {!props.readonly && <ActionButton icon="PlusIcon" variant="ghost" size="sm" className="self-start" onClick={() => handleAddPhone(cIdx)}>
                      {tc('action.add')}
                    </ActionButton>}
                </VStack>

                <VStack className="gap-2">
                  <Text variant="labelSmall" className="text-neutral-muted-on-fill">
                    {t('email')}
                  </Text>
                  {contact.emails.map((em, eIdx) => <HStack key={`${idPrefix}-wemail-${cIdx}-${eIdx}`} className="gap-2 items-center">
                      <Input width="100%" className="flex-1" value={em.email ?? ''} onChange={e => handleEmailChange(cIdx, eIdx, e.target.value)} disabled={props.readonly} type="email" />
                      {!props.readonly && contact.emails.length > 1 && <IconButton icon="Trash2Icon" variant="ghost" size="Small" className="shrink-0" onClick={() => handleRemoveEmail(cIdx, eIdx)} aria-label={tc('action.delete')} />}
                    </HStack>)}
                  {!props.readonly && <ActionButton icon="PlusIcon" variant="ghost" size="sm" className="self-start" onClick={() => handleAddEmail(cIdx)}>
                      {tc('action.add')}
                    </ActionButton>}
                </VStack>
              </VStack>

              <VStack className="flex-1 gap-2">
                <ContactAddressEdit contact={contact} onUpdate={patch => updateContact(cIdx, patch)} />
              </VStack>
            </HStack>
          </VStack>)}

        {!props.readonly && <ActionButton className="pl-0 self-start" icon="PlusIcon" onClick={handleAdd} variant="ghost" iconColor="primary">
            {tc('action.add')}
          </ActionButton>}
      </VStack>;
  };
  return <CFormItemBase {...props} readComponent={renderReadMode()} writeComponent={renderWriteMode()} />;
});